import { Controller } from "@hotwired/stimulus"
import { SECTIONS_PARAM, QUESTIONS_PARAM, FOCUS_PARAM } from "./reading_focus"

/**
 * Reading Link Builder (instructors only)
 *
 * Lets an instructor build an assigned-reading link for the chapter they are
 * looking at without knowing the token grammar: tick sections in a list built
 * from the chapter's own outline, pick which review questions to include, and
 * copy the resulting `?sections=…&questions=…` URL (see reading_focus.js and
 * docs/book-sections.md). The list is read from the DOM the chapter-outline
 * controller produced, so the numbers in the link are exactly the numbers the
 * student sees — the same guarantee the reading-focus feature relies on.
 *
 * Token choices, in order of what an instructor can read back:
 *  - a run of ticked sub-sections under one heading compresses to a range (0.1-0.8)
 *  - a ticked heading = whole section: its number, or its slug when un-numbered
 *  - a ticked "Intro" = the heading's lead-in only: N.0 when N is unique in the
 *    chapter, otherwise slug:intro (the Introduction has two un-numbered
 *    headings whose lead-ins would both be 0.0)
 *
 * Usage: assets/_reading_link_builder.html.erb renders the panel; the trigger is
 * the plain "Reading Link" item in the reader toolbar, which lives outside this
 * element (each reader has its own toolbar), so it is found by
 * [data-reading-link-builder-trigger] rather than as a Stimulus target.
 */
export default class extends Controller {
  static targets = ["panel"]

  connect() {
    this.built = false
    this.triggers = Array.from(document.querySelectorAll("[data-reading-link-builder-trigger]"))
    this.triggerHandler = (e) => {
      e.preventDefault()
      this.toggle()
    }
    this.triggers.forEach((t) => t.addEventListener("click", this.triggerHandler))
    this.keyHandler = (e) => {
      if (e.key === "Escape" && !this.panelTarget.hidden) this.close()
    }
    document.addEventListener("keydown", this.keyHandler)
    // The panel is rendered before the chapter, so chapter-outline has not run
    // yet. Wait a frame, then hide the toolbar item on assets that have no
    // outline — there is nothing to select, and reading focus itself only exists
    // where the outline controller runs (themed modules with h2/h3 structure).
    requestAnimationFrame(() => {
      if (document.querySelector(".chapter-outline")) return
      this.element.hidden = true
      this.triggers.forEach((t) => ((t.closest("[data-reading-link-builder-nav-item]") || t).hidden = true))
    })
  }

  disconnect() {
    document.removeEventListener("keydown", this.keyHandler)
    this.triggers?.forEach((t) => t.removeEventListener("click", this.triggerHandler))
  }

  toggle() {
    this.panelTarget.hidden ? this.open() : this.close()
  }

  open() {
    if (!this.built) this.#build()
    this.panelTarget.hidden = false
    this.triggers.forEach((t) => t.setAttribute("aria-expanded", "true"))
    this.#update()
    this.panelTarget.querySelector("input, button")?.focus()
  }

  close() {
    this.panelTarget.hidden = true
    this.triggers.forEach((t) => t.setAttribute("aria-expanded", "false"))
    this.triggers[0]?.focus()
  }

  // ---- reading the chapter ------------------------------------------------

  // One entry per outline item (plus synthetic "Intro" rows), in document order.
  #readOutline() {
    const items = Array.from(document.querySelectorAll(".chapter-outline li"))
      .filter((li) => !li.classList.contains("toc-intro"))
      .map((li) => {
        const a = li.querySelector("a")
        const number = a.querySelector(".toc-number")?.textContent.trim() || ""
        const slug = (a.getAttribute("href") || "").replace(/^#/, "")
        const title = a.textContent.replace(number, "").trim()
        return { number, slug, title, level: li.classList.contains("toc-sub") ? "h3" : "h2", heading: document.getElementById(slug) }
      })
      .filter((item) => item.heading)

    const quizSlugs = ["section-review-exercises", "section-suggested-solutions-to-review-exercises"]
    const majorCounts = {}
    items.forEach((item) => {
      if (item.level !== "h2") return
      item.major = item.number ? item.number.split(".")[0] : this.#majorOfChildren(items, item)
      if (item.major) majorCounts[item.major] = (majorCounts[item.major] || 0) + 1
    })

    const rows = []
    items.forEach((item, i) => {
      if (quizSlugs.includes(item.slug)) return
      const shortSlug = item.slug.replace(/^section-/, "")
      if (item.level === "h2") {
        const children = []
        for (let j = i + 1; j < items.length && items[j].level === "h3"; j++) children.push(items[j])
        const row = {
          kind: "section",
          label: item.number ? `${item.number} ${item.title}` : item.title,
          token: item.number ? item.number : shortSlug,
          item,
          children: []
        }
        rows.push(row)
        if (children.length && this.#hasLeadIn(item.heading)) {
          const uniqueMajor = item.major && majorCounts[item.major] === 1
          rows.push({
            kind: "intro",
            label: `${item.major ?? ""}.0 Intro`.trim(),
            token: uniqueMajor ? `${item.major}.0` : `${shortSlug}:intro`,
            parent: row
          })
        }
      } else {
        const parent = rows.filter((r) => r.kind === "section").pop()
        const row = { kind: "sub", label: `${item.number} ${item.title}`, token: item.number || shortSlug, parent }
        parent?.children.push(row)
        rows.push(row)
      }
    })
    return rows
  }

  #majorOfChildren(items, h2) {
    const next = items[items.indexOf(h2) + 1]
    return next && next.level === "h3" && next.number ? next.number.split(".")[0] : null
  }

  // Is there content between this heading and its first sub-heading?
  #hasLeadIn(heading) {
    const next = heading.nextElementSibling
    return Boolean(next) && !/^H[23]$/.test(next.tagName)
  }

  #questionCount() {
    const quiz = document.querySelectorAll(".chapter-quiz .quiz-question").length
    if (quiz) return Math.max(quiz, this.#authoredQuestionCount())
    return this.#authoredQuestionCount()
  }

  #authoredQuestionCount() {
    const heading = Array.from(document.querySelectorAll("h2")).find(
      (h) => h.textContent.trim().replace(/^[\d.\s]+/, "") === "Review Exercises"
    )
    let node = heading?.nextElementSibling
    while (node && node.tagName !== "OL") node = node.nextElementSibling
    return node ? node.children.length : 0
  }

  // ---- building the panel -------------------------------------------------

  #build() {
    this.rows = this.#readOutline()
    const questionCount = this.#questionCount()
    const panel = this.panelTarget
    panel.innerHTML = ""

    const head = document.createElement("div")
    head.className = "rlb-head"
    head.innerHTML = '<h2 class="rlb-title">Create a reading link</h2>'
    const closeBtn = document.createElement("button")
    closeBtn.type = "button"
    closeBtn.className = "rlb-close"
    closeBtn.setAttribute("aria-label", "Close")
    closeBtn.textContent = "×"
    closeBtn.addEventListener("click", () => this.close())
    head.appendChild(closeBtn)
    panel.appendChild(head)

    const intro = document.createElement("p")
    intro.className = "rlb-help"
    intro.textContent = "Tick what students should read. Everything else in the chapter folds up behind a “Not assigned” stub they can still open."
    panel.appendChild(intro)

    // sections
    const sectionsBox = document.createElement("fieldset")
    sectionsBox.className = "rlb-fieldset"
    sectionsBox.innerHTML = '<legend class="rlb-legend">Sections</legend>'
    this.rows.forEach((row) => {
      const label = document.createElement("label")
      label.className = `rlb-row rlb-row-${row.kind}`
      const box = document.createElement("input")
      box.type = "checkbox"
      box.className = "rlb-check"
      row.checkbox = box
      box.addEventListener("change", () => {
        if (row.kind === "section") {
          row.children.forEach((c) => (c.checkbox.checked = box.checked))
          const introRow = this.rows.find((r) => r.kind === "intro" && r.parent === row)
          if (introRow) introRow.checkbox.checked = box.checked
        } else if (row.parent && !box.checked) {
          row.parent.checkbox.checked = false
        }
        this.#update()
      })
      label.appendChild(box)
      const text = document.createElement("span")
      text.textContent = row.label
      label.appendChild(text)
      sectionsBox.appendChild(label)
    })
    if (!this.rows.length) {
      const none = document.createElement("p")
      none.className = "rlb-help"
      none.textContent = "This chapter has no numbered sections to choose from."
      sectionsBox.appendChild(none)
    }
    panel.appendChild(sectionsBox)

    // questions (only when the chapter has a review quiz)
    this.questionMode = "none"
    if (questionCount) panel.appendChild(this.#questionsFieldset(questionCount))

    // display option
    const dimLabel = document.createElement("label")
    dimLabel.className = "rlb-row rlb-row-option"
    this.dimBox = document.createElement("input")
    this.dimBox.type = "checkbox"
    this.dimBox.addEventListener("change", () => this.#update())
    dimLabel.appendChild(this.dimBox)
    const dimText = document.createElement("span")
    dimText.textContent = "Dim the rest instead of folding it up"
    dimLabel.appendChild(dimText)
    panel.appendChild(dimLabel)

    // result
    const result = document.createElement("div")
    result.className = "rlb-result"
    this.summary = document.createElement("p")
    this.summary.className = "rlb-summary"
    result.appendChild(this.summary)
    this.urlField = document.createElement("input")
    this.urlField.type = "text"
    this.urlField.readOnly = true
    this.urlField.className = "rlb-url"
    this.urlField.setAttribute("aria-label", "Reading link")
    this.urlField.addEventListener("focus", () => this.urlField.select())
    result.appendChild(this.urlField)
    const actions = document.createElement("div")
    actions.className = "rlb-actions"
    this.copyBtn = document.createElement("button")
    this.copyBtn.type = "button"
    this.copyBtn.className = "rlb-btn rlb-btn-primary"
    this.copyBtn.textContent = "Copy link"
    this.copyBtn.addEventListener("click", () => this.#copy())
    this.previewLink = document.createElement("a")
    this.previewLink.className = "rlb-btn"
    this.previewLink.target = "_blank"
    this.previewLink.rel = "noopener"
    this.previewLink.textContent = "Preview"
    actions.append(this.copyBtn, this.previewLink)
    result.appendChild(actions)
    panel.appendChild(result)

    this.built = true
  }

  #questionsFieldset(questionCount) {
    const qBox = document.createElement("fieldset")
    qBox.className = "rlb-fieldset"
    qBox.innerHTML = `<legend class="rlb-legend">Review questions${questionCount ? ` <span class="rlb-count">(${questionCount} in this chapter)</span>` : ""}</legend>`
    this.questionMode = "none"
    ;[
      ["none", "Not included"],
      ["all", "All questions"],
      ["some", "Only these:"]
    ].forEach(([value, text], i) => {
      const label = document.createElement("label")
      label.className = "rlb-row rlb-row-radio"
      const radio = document.createElement("input")
      radio.type = "radio"
      radio.name = "rlb-questions"
      radio.value = value
      radio.checked = i === 0
      radio.addEventListener("change", () => {
        this.questionMode = value
        this.questionInput.disabled = value !== "some"
        if (value === "some") this.questionInput.focus()
        this.#update()
      })
      label.appendChild(radio)
      const span = document.createElement("span")
      span.textContent = text
      label.appendChild(span)
      if (value === "some") {
        this.questionInput = document.createElement("input")
        this.questionInput.type = "text"
        this.questionInput.className = "rlb-text"
        this.questionInput.placeholder = "e.g. 1-5, 8-10"
        this.questionInput.disabled = true
        this.questionInput.setAttribute("aria-label", "Question numbers")
        this.questionInput.addEventListener("input", () => this.#update())
        label.appendChild(this.questionInput)
      }
      qBox.appendChild(label)
    })
    return qBox

  }

  // ---- the link -----------------------------------------------------------

  #sectionTokens() {
    const tokens = []
    this.rows.forEach((row) => {
      if (row.kind === "section") {
        if (row.checkbox.checked) {
          tokens.push(row.token)
          return
        }
        // runs of ticked sub-sections compress to ranges: 0.1-0.8
        const checked = row.children.filter((c) => c.checkbox.checked)
        let run = []
        const flush = () => {
          if (!run.length) return
          tokens.push(run.length > 2 ? `${run[0].token}-${run[run.length - 1].token}` : run.map((r) => r.token).join(","))
          run = []
        }
        row.children.forEach((c) => {
          if (!checked.includes(c)) return flush()
          const prev = run[run.length - 1]
          if (prev && !this.#consecutive(prev.token, c.token)) flush()
          run.push(c)
        })
        flush()
      } else if (row.kind === "intro" && row.checkbox.checked && !row.parent.checkbox.checked) {
        // keep document order: the intro comes right after its heading's slot
        tokens.splice(this.#tokenInsertIndex(tokens, row), 0, row.token)
      }
    })
    return tokens
  }

  #tokenInsertIndex(tokens, introRow) {
    // insert before any of this heading's sub-section tokens already emitted
    const firstChild = introRow.parent.children.find((c) => c.checkbox.checked)
    if (!firstChild) return tokens.length
    const idx = tokens.findIndex((t) => t === firstChild.token || t.startsWith(`${firstChild.token}-`) || t.startsWith(`${firstChild.token},`))
    return idx === -1 ? tokens.length : idx
  }

  #consecutive(a, b) {
    const [am, an] = a.split(".").map(Number)
    const [bm, bn] = b.split(".").map(Number)
    return am === bm && bn === an + 1
  }

  #questionsValue() {
    if (this.questionMode === "all") return "all"
    if (this.questionMode === "some") return this.questionInput?.value.replace(/\s+/g, "") || ""
    return ""
  }

  #update() {
    if (!this.built) return
    const sections = this.#sectionTokens()
    const questions = this.#questionsValue()
    const url = new URL(window.location.href)
    url.hash = ""
    url.searchParams.delete(SECTIONS_PARAM)
    url.searchParams.delete(QUESTIONS_PARAM)
    url.searchParams.delete(FOCUS_PARAM)
    if (sections.length) url.searchParams.set(SECTIONS_PARAM, sections.join(","))
    if (questions) url.searchParams.set(QUESTIONS_PARAM, questions)
    if (this.dimBox.checked && (sections.length || questions)) url.searchParams.set(FOCUS_PARAM, "dim")
    // keep the tokens readable in the pasted link
    const href = url.toString().replace(/%2C/g, ",").replace(/%3A/g, ":")

    const empty = !sections.length && !questions
    this.urlField.value = empty ? "" : href
    this.previewLink.href = empty ? "#" : href
    this.copyBtn.disabled = empty
    this.previewLink.classList.toggle("disabled", empty)
    const parts = []
    if (sections.length) parts.push(`Sections ${sections.join(", ").replace(/(\d)-(\d)/g, "$1–$2")}`)
    if (questions) parts.push(questions === "all" ? "all review questions" : `review questions ${questions}`)
    this.summary.textContent = empty ? "Nothing selected yet — the link would show the whole chapter." : parts.join(" · ")
  }

  async #copy() {
    const text = this.urlField.value
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      this.urlField.focus()
      this.urlField.select()
      document.execCommand?.("copy")
    }
    const label = this.copyBtn.textContent
    this.copyBtn.textContent = "Copied!"
    setTimeout(() => (this.copyBtn.textContent = label), 1600)
  }
}
